<template>
  <div class="space-y-8">
    <!-- Categories Section -->
    <div v-for="category in filteredCategories" :key="category.id" class="space-y-4">
      <!-- Category Header -->
      <div class="tw-flex items-center justify-between">
        <div class="tw-flex items-center w-full">
          <h4 class="text-neutral-400 whitespace-nowrap mr-4">{{ category.name }}</h4>
          <div class="h-px bg-gray-300 flex-grow"></div>
        </div>
        <div class="tw-flex items-center space-x-2 ml-4">
          <button
            v-if="category.articles.length > 4"
            @click="viewCategoryGrid(category)"
            class="text-sm text-primary hover:text-primary-lightest font-medium transition-colors whitespace-nowrap"
          >
            {{ $t('knowledge_base_view_all') }} ({{ category.articles.length }})
          </button>
          <Icon
            name="trash"
            size="14"
            class="fill-red-400 hover:fill-red-600 cursor-pointer"
            v-if="isOwner && !category.isUncategorized"
            @click="deleteCategoryClick(category)"
          />
          <Icon
            name="settings"
            size="14"
            class="fill-gray-400 hover:fill-gray-600 cursor-pointer"
            v-if="isOwner && !category.isUncategorized"
            @click="editCategoryClick(category)"
          />
        </div>
      </div>

      <!-- Articles Grid/Slider -->
      <div v-if="category.articles.length > 0 || isEditMode">
        <!-- Edit Mode: Drag and Drop Grid -->
        <div v-if="isEditMode">
          <draggable
            v-model="category.articles"
            group="posts"
            item-key="id"
            class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 min-h-[200px] p-4 border-2 border-dashed border-gray-200 rounded-lg transition-colors"
            :class="{ 'border-primary bg-primary/5': isDragOver }"
            :data-category-id="category.isUncategorized ? 'uncategorized' : category.id"
            @start="onDragStart"
            @end="onDragEnd"
            @change="(evt) => onPostMoved(evt, category)"
            :animation="200"
          >
            <template #item="{ element: article }">
              <div class="drag-item">
                <KnowledgeBasePostCard
                  :post="article.postData"
                  :key="article.id"
                  :show-actions="true"
                  :is-logged-in="Boolean(isLoggedIn)"
                  :community-id="communityId"
                  :spotting-area-id="spottingAreaId"
                  :current-user-id="userId"
                  @edit-post="editPostClick"
                  @delete-post="deletePostClick"
                  @bookmark-toggled="handleBookmarkToggled"
                  class="h-96 post-card cursor-move"
                />
              </div>
            </template>
            <template #header>
              <div v-if="category.articles.length === 0" class="col-span-full text-center py-8">
                <Icon name="upload" class="w-12 h-12 text-gray-400 mx-auto mb-3"/>
                <p class="text-gray-500">
                  {{ $t('knowledge_base_drag_posts_here') }}
                </p>
              </div>
            </template>
          </draggable>
        </div>

        <!-- Normal Mode: Regular Grid/Slider -->
        <div v-else-if="category.articles.length > 0">
          <!-- Regular Grid for 4 or fewer articles -->
          <div v-if="category.articles.length <= 4"
               class="grid grid-cols-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-3">
            <KnowledgeBasePostCard
              v-for="article in category.articles"
              :post="article.postData"
              :key="article.id"
              :show-actions="true"
              :is-logged-in="Boolean(isLoggedIn)"
              :community-id="communityId"
              :current-user-id="userId"
              @edit-post="editPostClick"
              @delete-post="deletePostClick"
              @bookmark-toggled="handleBookmarkToggled"
              class="h-96 post-card"
            />
          </div>

          <!-- Slider for 5+ articles -->
          <div v-else class="relative">
            <div class="overflow-hidden kb-slider-container">
              <div
                class="tw-flex transition-transform duration-300 ease-in-out gap-3"
                :style="{ transform: `translateX(-${getSliderOffset(category.id)}px)` }"
              >
                <div
                  v-for="article in category.articles"
                  :key="article.id"
                  class="flex-shrink-0"
                  :style="{ width: getCardWidth() }"
                >
                  <KnowledgeBasePostCard
                    :post="article.postData"
                    :show-actions="true"
                    :is-logged-in="Boolean(isLoggedIn)"
                    :current-user-id="userId"
                    :community-id="communityId"
                    @edit-post="editPostClick"
                    @delete-post="deletePostClick"
                    @bookmark-toggled="handleBookmarkToggled"
                    class="h-96 post-card"
                  />
                </div>
              </div>
            </div>

            <!-- Navigation buttons -->
            <button
              v-if="canScrollLeft(category.id)"
              @click="scrollLeft(category.id)"
              class="absolute left-0 top-1/2 -translate-y-1/2 max-h-10 min-w-10 max-w-10 -translate-x-2 bg-white rounded-full shadow-lg border p-2 hover:bg-gray-50 transition-colors z-10"
            >
              <Icon name="chevron-left" class="w-5 h-5 text-gray-600"/>
            </button>

            <button
              v-if="canScrollRight(category.id, category.articles.length)"
              @click="scrollRight(category.id, category.articles.length)"
              class="absolute right-0 top-1/2 -translate-y-1/2 max-h-10 min-w-10 max-w-10 translate-x-2 bg-white rounded-full shadow-lg border p-2 hover:bg-gray-50 transition-colors z-10"
            >
              <Icon name="chevron-right" class="w-5 h-5 text-gray-600"/>
            </button>
          </div>
        </div>
      </div>

      <!-- Empty Category State -->
      <div v-else class="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
        <div class="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <Icon name="file" class="w-6 h-6 text-gray-400"/>
        </div>
        <p class="text-sm text-gray-500">{{ $t('knowledge_base_no_documents_category') }}</p>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoadingPosts || isLoadingCategories" class="text-center py-16">
      <LoadingNew/>
    </div>

    <!-- Overall Empty State -->
    <div v-else-if="!hasAnyArticles" class="text-center py-16">
      <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <Icon name="book" class="w-8 h-8 text-gray-400"/>
      </div>
      <h3 class="text-lg font-semibold text-gray-900 mb-2">{{ $t('knowledge_base_no_articles_yet') }}</h3>
      <p class="text-gray-500 max-w-md mx-auto">
        {{ $t('knowledge_base_start_building_description') }}
      </p>
    </div>


  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import Icon from '@/components/Icon/Icon.vue'
  import LoadingNew from '@/components/Loaders/LoadingNew.vue'
  import TranslationsMixin from '@/util/TranslationsMixin'
  import UserMixin from '@/util/UserMixin'
  import MODAL_IDS from '@/constants/modal-ids.js'
  import { MUTATION_TYPES } from '@/store/modules/ui'
  import {
    deleteCategory as deleteCategoryApi,
    deletePost as deletePostApi,
    fetchKnowledgeBaseCategories,
    fetchPosts,
    fetchPostsByCategory,
    fetchUncategorizedPosts,
    reorderCategories,
    searchPosts,
    updatePostCategory
  } from '@/Domain/KnowledgeBase/Api/knowledgeBase'
  import KnowledgeBasePostCard from '@/Domain/KnowledgeBase/Components/KnowledgeBasePostCard.vue'
  import CommunityMixin from '@/util/CommunityMixin'
  import { trackMatomoEvent } from '@/util/analytics'
  import { MATOMO_EVENT_ACTIONS, MATOMO_EVENT_CATEGORIES } from '@/constants/analytics-constants'
  import draggable from 'vuedraggable'

  export default defineComponent({
    name: 'KnowledgeBaseContent',
    mixins: [TranslationsMixin, UserMixin],
    emits: ['view-category'],
    components: {
      KnowledgeBasePostCard,
      Icon,
      LoadingNew,
      draggable,
    },
    props: {
      searchQuery: {
        type: String,
        default: ''
      },
      categoryFilter: {
        type: [String, Number],
        default: null
      },
      bookmarkedOnly: {
        type: Boolean,
        default: false
      },
      communityId: {
        type: Number,
        default: null
      },
      spottingAreaId: {
        type: Number,
        default: null,
      },
      isEditMode: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        hideEmptyCategories: true,
        showPostsOnly: false,
        visibilityFilter: null, // null, true (public), false (private)
        customCategoryOrder: [], // Array of category IDs in custom order
        useCustomOrder: false, // Whether to use custom order or alphabetical (set to true when categories are reordered)
        // Legacy articles that don't belong to any specific category (deprecated, keeping for transition)
        uncategorizedArticles: [],
        // Posts data
        posts: [],
        realCategories: [], // Categories loaded from API
        isLoadingPosts: false,
        isLoadingCategories: false,
        // Slider state for each category
        sliderOffsets: {}, // Track slider position for each category
        // Drag and drop state
        isDragOver: false,
        draggedItem: null,
      }
    },
    created() {
      this.$bus.on('confirmAction', this.handleConfirmAction)
    },
    beforeUnmount() {
      this.$bus.off('confirmAction', this.handleConfirmAction)
    },
    computed: {
      canViewAllKnowledgeBaseContentTypes() {
        return this.$store.getters.canViewAllKnowledgeBaseContentTypes
      },
      canViewPreviewKnowledgeBaseContentTypes() {
        return this.$store.getters.canViewPreviewKnowledgeBaseContentTypes
      },
      canViewDetailKnowledgeBaseContentTypes() {
        return this.$store.getters.canViewDetailKnowledgeBaseContentTypes
      },
      hasAnyArticles() {
        // We have articles if we have any posts (categorized or uncategorized)
        return this.posts.length > 0
      },
      allCategories() {
        // Use only real categories from API
        const mergedCategories = this.realCategories.map(realCat => {
          // Get posts for this category
          const categoryPosts = this.posts.filter(post =>
            post.knowledgeBaseCategoryId === realCat.id &&
            (this.visibilityFilter === null || post.isPublic === this.visibilityFilter)
          )

          // Convert posts to article format for display
          const postsAsArticles = categoryPosts.map(post => ({
            id: `post-${post.id}`,
            title: post.title,
            description: post.excerpt || this.stripHtml(post.body).substring(0, 150) + '...',
            status: post.isPublic ? 'published' : 'draft',
            visibility: post.isPublic ? 'public' : 'private',
            views: 0,
            likes: 0,
            updated_at: post.updatedAt,
            featured_image: !!post.thumbnail,
            author: post.creator,
            isPost: true,
            postData: post
          }))

          return {
            id: realCat.id,
            name: realCat.name,
            description: realCat.description,
            articles: postsAsArticles,
            isReal: true
          }
        })

        // Create the "Uncategorized" virtual category
        const uncategorizedPosts = this.posts.filter(post =>
          !post.knowledgeBaseCategoryId &&
          (this.visibilityFilter === null || post.isPublic === this.visibilityFilter)
        )

        const uncategorizedPostsAsArticles = uncategorizedPosts.map(post => ({
          id: `post-${post.id}`,
          title: post.title,
          description: post.excerpt || this.stripHtml(post.body).substring(0, 150) + '...',
          status: post.isPublic ? 'published' : 'draft',
          visibility: post.isPublic ? 'public' : 'private',
          views: 0,
          likes: 0,
          updated_at: post.updatedAt,
          featured_image: !!post.thumbnail,
          author: post.creator,
          isPost: true,
          postData: post
        }))

        const uncategorizedCategory = {
          id: 0,
          name: this.$t('knowledge_base_uncategorized'),
          articles: uncategorizedPostsAsArticles, // Only use posts from knowledge_base_posts, not legacy articles
          isUncategorized: true
        }

        let sortedCategories;

        if (this.useCustomOrder && this.customCategoryOrder.length > 0) {
          // Use custom order
          const categoryMap = new Map(mergedCategories.map(cat => [cat.id, cat]))
          sortedCategories = this.customCategoryOrder
            .map(id => categoryMap.get(id))
            .filter(Boolean) // Remove any undefined categories

          // Add any categories not in custom order at the end, alphabetically
          const orderedIds = new Set(this.customCategoryOrder)
          const remainingCategories = mergedCategories
            .filter(cat => !orderedIds.has(cat.id))
            .sort((a, b) => a.name.localeCompare(b.name))

          sortedCategories = [...sortedCategories, ...remainingCategories]
        } else {
          // Sort alphabetically by name
          sortedCategories = [...mergedCategories].sort((a, b) => a.name.localeCompare(b.name))
        }

        // Return sorted categories first, then uncategorized at the bottom
        return [...sortedCategories, uncategorizedCategory]
      },
      filteredCategories() {
        let filtered = this.allCategories;

        // If categoryFilter is set, only show that specific category or uncategorized
        if (this.categoryFilter !== null) {
          if (this.categoryFilter === 'uncategorized') {
            filtered = filtered.filter(category => category.isUncategorized)
          } else {
            filtered = filtered.filter(category => category.id === this.categoryFilter)
          }
        }

        // Apply empty category filter - in edit mode, always show empty categories
        if (this.hideEmptyCategories && !this.isEditMode) {
          filtered = filtered.filter(category => category.articles.length > 0)
        }

        return filtered
      }
    },
    watch: {
      searchQuery() {
        this.loadPosts()
      },
      categoryFilter() {
        this.loadPosts()
        this.loadRealCategories()
      },
      bookmarkedOnly() {
        this.loadPosts()
      }
    },
    mounted() {
      // Listen for category added event from modal
      this.$bus.on('category-added', this.handleCategoryAdded)
      this.$bus.on('categories-reordered', this.handleCategoriesReordered)
      this.$bus.on('category-updated', this.handleCategoryUpdated)
      this.$bus.on('post-created', this.handlePostCreated)
      this.$bus.on('post-updated', this.handlePostUpdated)

      // Initialize slider offsets
      this.initializeSliderOffsets()

      // Listen for window resize to recalculate slider positions
      window.addEventListener('resize', this.handleResize)

      // Load both categories and posts
      Promise.all([
        this.loadRealCategories(),
        this.loadPosts()
      ]).then(() => {
      })
    },
    beforeUnmount() {
      // Clean up event listeners
      this.$bus.off('category-added', this.handleCategoryAdded)
      this.$bus.off('categories-reordered', this.handleCategoriesReordered)
      this.$bus.off('category-updated', this.handleCategoryUpdated)
      this.$bus.off('post-created', this.handlePostCreated)
      this.$bus.off('post-updated', this.handlePostUpdated)
      window.removeEventListener('resize', this.handleResize)
    },
    methods: {
      handleConfirmAction(modalContext) {
        if (modalContext && modalContext.modalContextType === 'knowledge-base-category') {
          this.confirmDeleteCategory(modalContext.category)
        } else if (modalContext && modalContext.modalContextType === 'knowledge-base-post') {
          this.confirmDeletePost(modalContext.post)
        }
      },
      async confirmDeleteCategory(category) {
        try {
          await deleteCategoryApi(category.id)
          await this.loadRealCategories()

          // Track category deletion
          trackMatomoEvent(MATOMO_EVENT_CATEGORIES.KNOWLEDGE_BASE, MATOMO_EVENT_ACTIONS.DELETE_CATEGORY, category.name)

          this.$notify({
            type: 'success',
            title: this.$t('success'),
            text: this.$t('knowledge_base_category_deleted_successfully', { name: category.name })
          })
        } catch (error) {
          console.error('Failed to delete category:', error)
          this.$notify({
            type: 'error',
            title: this.$t('error'),
            text: this.$t('knowledge_base_category_delete_failed')
          })
        }
      },
      async confirmDeletePost(article) {
        try {
          await deletePostApi(article.postData.id)
          await this.loadPosts()
          await this.loadRealCategories() // Refresh category counts

          // Track post deletion
          trackMatomoEvent(MATOMO_EVENT_CATEGORIES.KNOWLEDGE_BASE, MATOMO_EVENT_ACTIONS.DELETE_KNOWLEDGE_BASE_RESOURCE, article.title)

          this.$notify({
            type: 'success',
            title: this.$t('success'),
            text: this.$t('knowledge_base_post_deleted_successfully', { title: article.title })
          })
        } catch (error) {
          console.error('Failed to delete post:', error)
          this.$notify({
            type: 'error',
            title: this.$t('error'),
            text: this.$t('knowledge_base_post_delete_failed')
          })
        }
      },
      isAccessibleForUser(article) {
        // @TODO check on real permissions
        return true
      },
      handleCategoryAdded(categoryData) {
        // Reload categories from API to get fresh data
        this.loadRealCategories()
      },
      async handleCategoriesReordered(orderData) {
        this.customCategoryOrder = orderData.order
        this.useCustomOrder = true // Always use custom order after reordering

        // Call API to save the new order to the database
        try {
          await reorderCategories(orderData.order)
          // Refresh categories to get updated order from database
          await this.loadRealCategories()
        } catch (error) {
          console.error('Failed to save category order:', error)
          // You could show a toast notification here
        }
      },

      handleCategoryUpdated(categoryData) {
        this.loadRealCategories()
      },

      handlePostCreated(postData) {
        this.loadPosts()
        this.loadRealCategories() // Refresh category counts
      },

      handlePostUpdated(postData) {
        this.loadPosts()
      },
      getFileTypeFromMimeType(mimeType) {
        if (!mimeType) return 'unknown'

        const mimeTypeMap = {
          'image/jpeg': 'jpg',
          'image/png': 'png',
          'image/gif': 'gif',
          'image/webp': 'webp',
          'image/svg+xml': 'svg',
          'application/pdf': 'pdf',
          'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
          'application/vnd.ms-powerpoint': 'ppt',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
          'application/vnd.ms-excel': 'xls',
          'application/msword': 'doc',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
          'audio/mpeg': 'mp3',
          'audio/mp3': 'mp3',
          'video/mp4': 'mp4',
          'text/csv': 'csv',
          'text/plain': 'txt',
          'application/zip': 'zip',
          'link': 'url' // For URL links
        }

        return mimeTypeMap[mimeType] || 'unknown'
      },
      getFileUrlFromItem(item) {
        // Check if it's a URL link
        const urlMeta = item.meta?.find(meta => meta.key === 'url')
        if (urlMeta?.value && urlMeta.value.trim()) {
          return urlMeta.value
        }

        // For file uploads, use the sql_media_id to construct the API URL
        if (item.sql_media_id) {
          return `https://local.datascouts.test/api/files/${item.sql_media_id}`
        }

        return null
      },
      getFileNameFromTitle(title, fileType) {
        if (!title) return `document.${fileType}`

        // Clean title for filename
        const cleanTitle = title.replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, '-').toLowerCase()
        return `${cleanTitle}.${fileType}`
      },
      formatUnixTimestamp(timestamp) {
        if (!timestamp) return new Date().toISOString()

        // Convert from Unix timestamp to ISO string
        const date = new Date(timestamp * 1000)
        return date.toISOString()
      },
      estimateFileSize(fileType) {
        // Rough estimates based on file type
        const sizeMap = {
          'jpg': 250000,
          'png': 180000,
          'pdf': 1500000,
          'pptx': 890000,
          'xlsx': 45000,
          'mp3': 3500000,
          'mp4': 8500000,
          'csv': 15000,
          'zip': 2500000
        }

        return sizeMap[fileType] || 100000
      },
      isImageType(fileType) {
        return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(fileType?.toLowerCase())
      },
      checkIfBookmarked(isLikedBy) {
        // Check if current user has liked this item
        // For now, just check if the array exists and has items
        return isLikedBy && isLikedBy.length > 0
      },
      // Slider methods
      getCardWidth() {
        // Calculate card width based on screen size - we want to show 4.5 cards (half visible 5th)
        // Using Tailwind breakpoints: md:, lg:, xl:
        if (window.innerWidth >= 1280) { // xl breakpoint
          return 'calc(25% - 9px)' // 4 cards with gap
        } else if (window.innerWidth >= 1024) { // lg breakpoint
          return 'calc(25% - 9px)' // 4 cards with gap
        } else if (window.innerWidth >= 768) { // md breakpoint
          return 'calc(33.333% - 8px)' // 3 cards with gap
        } else {
          return 'calc(33.333% - 8px)' // 3 cards with gap on mobile
        }
      },
      getSliderOffset(categoryId) {
        return this.sliderOffsets[categoryId] || 0
      },
      getCardsPerView() {
        // Return how many cards to scroll at once
        if (window.innerWidth >= 1024) { // lg and xl
          return 4
        } else { // md and below
          return 3
        }
      },
      scrollLeft(categoryId) {
        const cardsPerView = this.getCardsPerView()
        const scrollAmount = this.getScrollAmount()

        const currentOffset = this.sliderOffsets[categoryId] || 0
        const newOffset = Math.max(0, currentOffset - scrollAmount)

        this.sliderOffsets[categoryId] = newOffset
      },
      scrollRight(categoryId, totalArticles) {
        const cardsPerView = this.getCardsPerView()
        const scrollAmount = this.getScrollAmount()

        const currentOffset = this.sliderOffsets[categoryId] || 0
        const maxOffset = this.getMaxOffset(totalArticles, cardsPerView)
        const newOffset = Math.min(maxOffset, currentOffset + scrollAmount)

        this.sliderOffsets[categoryId] = newOffset
      },
      getScrollAmount() {
        // Scroll by the width of the visible cards per view
        const sliderContainer = document.querySelector('.kb-slider-container') as HTMLElement
        const containerWidth = sliderContainer?.offsetWidth || 1200
        const cardsPerView = this.getCardsPerView()
        const gap = 12 // gap-3 = 0.75rem = 12px

        // Calculate the exact width including gaps, matching the CSS calculation
        // Each card is: (containerWidth - (cardsPerView-1) * gap) / cardsPerView + gap
        const cardWidth = (containerWidth - (cardsPerView - 1) * gap) / cardsPerView
        const scrollAmount = cardWidth + gap

        return Math.floor(scrollAmount) // Floor to avoid fractional pixels
      },
      getMaxOffset(totalArticles, cardsPerView) {
        // Calculate the maximum offset to ensure we don't scroll past the end
        const scrollAmount = this.getScrollAmount()

        // Maximum offset is when we show the last cardsPerView articles
        const maxScrollSteps = Math.max(0, totalArticles - cardsPerView)
        return maxScrollSteps * scrollAmount
      },
      canScrollLeft(categoryId) {
        return (this.sliderOffsets[categoryId] || 0) > 0
      },
      canScrollRight(categoryId, totalArticles) {
        const cardsPerView = this.getCardsPerView()
        const currentOffset = this.sliderOffsets[categoryId] || 0
        const maxOffset = this.getMaxOffset(totalArticles, cardsPerView)
        return currentOffset < maxOffset
      },
      initializeSliderOffsets() {
        // Initialize slider offsets for all categories
        this.allCategories.forEach(category => {
          this.sliderOffsets[category.id] = 0
        })
      },
      handleResize() {
        // Reset all slider positions on resize to avoid layout issues
        this.initializeSliderOffsets()
      },

      // New methods for posts and categories integration
      async loadRealCategories() {
        this.isLoadingCategories = true
        try {
          await fetchKnowledgeBaseCategories()
            .then((response) => {
              // Categories are already ordered by the backend (by 'order' field, then by 'name')
              this.realCategories = response.data

              // Set up the category order based on the backend ordering
              if (this.realCategories && this.realCategories.length > 0) {
                // Categories are already ordered by the backend, so preserve that order
                this.customCategoryOrder = this.realCategories.map(cat => cat.id)
                this.useCustomOrder = true
              }
            })

        } catch (error) {
          console.error('Failed to load categories:', error)
        } finally {
          this.isLoadingCategories = false
        }
      },

      async loadPosts() {
        this.isLoadingPosts = true
        try {
          const params = {
            per_page: 300,
            bookmarked_only: this.bookmarkedOnly,
            communityId: this.communityId,
            spottingAreaId: this.spottingAreaId
          }

          let response

          // Use backend filtering for bookmarked posts to support proper pagination

          // If search query is provided, use search endpoint
          if (this.searchQuery && this.searchQuery.trim()) {
            response = await searchPosts({
              q: this.searchQuery.trim(),
              bookmarked_only: this.bookmarkedOnly,
              communityId: this.communityId,
              spottingAreaId: this.spottingAreaId
            })
          }
          // Fetch of posts through a category happens through a dedicated endpoint
          else if (this.categoryFilter !== null) {
            if (this.categoryFilter === 'uncategorized') {
              response = await fetchUncategorizedPosts(params)
            } else {
              response = await fetchPostsByCategory({
                ...params,
                category_id: this.categoryFilter
              })
            }
          }
          // Otherwise load all posts
          else {
            response = await fetchPosts(params)
          }

          const data = response.data

          // Handle different response structures
          if (data.data && Array.isArray(data.data)) {
            // Paginated response (category-specific or search)
            this.posts = data.data
          } else if (data.data && data.data.posts) {
            // Structured response (all posts)
            this.posts = data.data.posts
          } else {
            this.posts = []
          }
          this.isLoadingPosts = false
        } catch (error) {
          console.error('Failed to load posts:', error)
        }
      },

      stripHtml(html) {
        if (!html) return ''
        return html.replace(/<[^>]*>/g, '')
      },

      openCreatePostModal() {
        const modalContext = {
          categories: this.realCategories,
          isEditing: false
        }
        this.$store.commit(MUTATION_TYPES.SET_MODAL_CONTEXT, modalContext)
        this.$store.commit(MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.CREATE_KNOWLEDGE_BASE_POST)
      },

      editCategoryClick(category) {
        // Track category edit
        trackMatomoEvent(MATOMO_EVENT_CATEGORIES.KNOWLEDGE_BASE, MATOMO_EVENT_ACTIONS.EDIT_CATEGORY, category.name)

        const modalContext = {
          category: category,
          isEditing: true
        }
        this.$store.commit(MUTATION_TYPES.SET_MODAL_CONTEXT, modalContext)
        this.$store.commit(MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.EDIT_KNOWLEDGE_BASE_CATEGORY)
      },

      deleteCategoryClick(category) {
        const modalContext = {
          title: this.$t('knowledge_base_delete_category_title', { name: category.name }),
          body: this.$t('knowledge_base_delete_category_confirm'),
          confirmLabel: this.$t('delete'),
          cancelLabel: this.$t('cancel'),
          isCancellable: true,
          modalContextType: 'knowledge-base-category',
          category: category
        }
        this.$store.commit(MUTATION_TYPES.SET_MODAL_CONTEXT, modalContext)
        this.$store.commit(MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.CONFIRMATION)
      },

      editPostClick(article) {
        // Track post edit
        trackMatomoEvent(MATOMO_EVENT_CATEGORIES.KNOWLEDGE_BASE, MATOMO_EVENT_ACTIONS.EDIT_POST, article.title)

        const modalContext = {
          post: article.postData,
          categories: this.realCategories,
          isEditing: true
        }
        this.$store.commit(MUTATION_TYPES.SET_MODAL_CONTEXT, modalContext)
        this.$store.commit(MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.EDIT_KNOWLEDGE_BASE_POST)
      },

      deletePostClick(article) {
        const modalContext = {
          title: this.$t('knowledge_base_delete_post_title', { title: article.title }),
          body: this.$t('knowledge_base_delete_post_confirm'),
          confirmLabel: this.$t('delete'),
          cancelLabel: this.$t('cancel'),
          isCancellable: true,
          modalContextType: 'knowledge-base-post',
          post: article
        }
        this.$store.commit(MUTATION_TYPES.SET_MODAL_CONTEXT, modalContext)
        this.$store.commit(MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.CONFIRMATION)
      },

      handleBookmarkToggled(updatedPost) {
        // Update the post data in our posts array
        const postIndex = this.posts.findIndex(post => post.id === updatedPost.id)
        if (postIndex !== -1) {
          this.posts[postIndex].isBookmarked = updatedPost.is_bookmarked
          this.posts[postIndex].bookmarks_count = updatedPost.bookmarks_count
        }
      },

      viewCategoryGrid(category, isCategoryId = false) {
        // Track category grid view
        const categoryName = category.isUncategorized ? this.$t('knowledge_base_uncategorized') : (category.name || this.$t('unknown'))
        trackMatomoEvent(MATOMO_EVENT_CATEGORIES.KNOWLEDGE_BASE, MATOMO_EVENT_ACTIONS.VIEW_CATEGORY_GRID, categoryName)

        // Handle uncategorized posts separately
        if (category.isUncategorized) {
          this.$emit('view-category', null) // null means show uncategorized posts
        } else {

          if (isCategoryId) {
            const realCategory = this.realCategories.find(cat => cat.id === category)
            this.$emit('view-category', realCategory)

            return
          }

          // Find the real category data instead of the computed one
          const realCategory = this.realCategories.find(cat => cat.id === category.id)
          this.$emit('view-category', realCategory || category)
        }
      },

      // Drag and drop methods
      onDragStart(evt) {
        this.isDragOver = true
        this.draggedItem = evt.item
      },

      onDragEnd(evt) {
        this.isDragOver = false
        this.draggedItem = null
      },

      async onPostMoved(evt, targetCategory) {
        // evt.added: element was added to this category
        // evt.removed: element was removed from this category
        // evt.moved: element was moved within this category

        if (evt.added) {
          const movedPost = evt.added.element
          const targetCategoryId = targetCategory.isUncategorized ? null : targetCategory.id

          await this.updatePostCategory(movedPost.postData.id, targetCategoryId)

          // Show success message
          this.$store.commit('ui/SHOW_NOTIFICATION', {
            type: 'success',
            message: this.$t('knowledge_base_post_moved_success'),
            timeout: 3000
          })
        }

        if (evt.removed) {
          // Post was removed from this category - handled by the category it was added to
        }
      },

      getCurrentCategoryId(element) {
        // Check if element is valid
        if (!element || typeof element.closest !== 'function') {
          console.warn('Invalid element passed to getCurrentCategoryId:', element)
          return undefined
        }

        // Find the category ID from the draggable element
        // We need to traverse up the DOM to find the category container
        let categoryElement = element.closest('[data-category-id]')
        if (categoryElement) {
          const categoryId = categoryElement.getAttribute('data-category-id')
          return categoryId === 'uncategorized' ? null : parseInt(categoryId)
        }

        console.warn('Could not find category ID for element:', element)
        return undefined
      },

      async updatePostCategory(postId, categoryId) {
        try {
          await updatePostCategory(postId, categoryId)

          // Reload posts to reflect changes
          await this.loadPosts()
        } catch (error) {
          console.error('Failed to update post category:', error)
          this.$store.commit('ui/SHOW_NOTIFICATION', {
            type: 'error',
            message: this.$t('knowledge_base_post_move_error'),
            timeout: 5000
          })
        }
      }
    }
  })
</script>
